import { enqueueSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { IOrgCandidateListItem } from 'src/services/candidates/candidates.types';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

interface ListCandidateRowActionsProps {
  row: IOrgCandidateListItem;
  onView: (candidateId: string) => void;
  onRemoveFromList: (row: IOrgCandidateListItem) => void;
  isRemoving?: boolean;
}

/**
 * Row kebab for a candidate inside a saved list: View, copy email / phone, and Remove from list —
 * the same set the list offered before, minus the actions that belonged to the candidates page
 * proper (add to job, export, invite) rather than to list membership.
 *
 * Deliberately NOT CandidateRowActions, whose Remove deletes the candidate record. Here the
 * destructive action only severs this list membership.
 */
export default function ListCandidateRowActions({ row, onView, onRemoveFromList, isRemoving }: ListCandidateRowActionsProps) {
  const { t } = useTranslate();
  const popover = usePopover();

  const confirm = useBoolean();

  const copy = (text?: string | null) => {
    popover.onClose();
    if (!text) return;
    navigator.clipboard.writeText(text);
    enqueueSnackbar(t('common.copied_to_clipboard'), { variant: 'success' });
  };

  return (
    <>
      <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
        <Iconify icon="eva:more-horizontal-fill" />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="right-top" sx={{ width: 200 }}>
        <MenuItem onClick={() => { popover.onClose(); onView(row.id); }}>
          <Iconify icon="solar:eye-bold" />
          <Typography variant="body2">{t('common.view')}</Typography>
        </MenuItem>

        {row.email && (
          <MenuItem onClick={() => copy(row.email)}>
            <Iconify icon="mdi:email-outline" />
            <Typography variant="body2">{t('candidates.detail.actions.copy_email')}</Typography>
          </MenuItem>
        )}

        {row.phone_number && (
          <MenuItem onClick={() => copy(row.phone_number)}>
            <Iconify icon="solar:phone-linear" />
            <Typography variant="body2">{t('candidates.detail.actions.copy_phone')}</Typography>
          </MenuItem>
        )}

        <MenuItem sx={{ color: 'error.main' }} onClick={() => { popover.onClose(); confirm.onTrue(); }}>
          <Iconify icon="ix:remove-circle-filled" />
          <Typography variant="body2">{t('lists.actions.remove_from_list')}</Typography>
        </MenuItem>
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title={t('lists.actions.remove_from_list')}
        content={t('lists.actions.remove_from_list_confirm', { count: 1 })}
        action={
          <LoadingButton
            loading={isRemoving}
            variant="contained"
            color="error"
            onClick={() => { onRemoveFromList(row); confirm.onFalse(); }}
          >
            {t('common.remove')}
          </LoadingButton>
        }
      />
    </>
  );
}
